<template>
  <div class="journey" role="img" :aria-label="ariaLabel">
    <p class="phase-now">
      Phase: <strong>{{ current.label }}</strong>
    </p>
    <div class="line-wrap">
      <div class="line">
        <div class="fill" :style="{ width: fillPct }" />
      </div>
      <ol class="stages">
        <li
          v-for="(stage, i) in stages"
          :key="stage.id"
          class="stage"
          :class="{
            done: i < activeIndex,
            current: i === activeIndex,
            upcoming: i > activeIndex,
          }"
        >
          <span class="tick" />
          <span class="label">{{ stage.label }}</span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const JOURNEY_STAGES = [
  { id: 'clarify', label: 'Clarify', slideIds: ['00', '01a', '01', '02', '03', '04', '04a', '04b', '05', '06', '09', '07', '08', '08a'] },
  { id: 'design', label: 'Design', slideIds: ['10', '11', '12', '12a', '13', '14', '15', '15a'] },
  { id: 'build', label: 'Build & review', slideIds: ['15b', '15c', '16', '17'] },
  { id: 'prove', label: 'Prove & release', slideIds: ['18', '18a', '19', '20', '21', '22'] },
  { id: 'operate', label: 'Observe & learn', slideIds: ['23', '24', '25', '26', '26a'] },
]

const props = defineProps({
  slideId: { type: String, required: true },
})

const stages = JOURNEY_STAGES

const activeIndex = computed(() => {
  const idx = stages.findIndex((s) => s.slideIds.includes(props.slideId))
  return idx >= 0 ? idx : 0
})

const current = computed(() => stages[activeIndex.value])

const fillPct = computed(() => {
  if (stages.length <= 1) return '0%'
  // Fill to center of current stage tick
  const pct = (activeIndex.value / (stages.length - 1)) * 100
  return `${Math.max(pct, 2)}%`
})

const ariaLabel = computed(
  () => `Delivery phase ${current.value.label}: ${activeIndex.value + 1} of ${stages.length}`,
)
</script>

<style scoped>
.journey {
  flex: 1;
  min-width: 0;
  padding: 0 0.75rem;
}

.phase-now {
  margin: 0 0 0.45rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
  text-align: center;
}

.phase-now strong {
  color: var(--teal-deep);
  font-weight: 700;
}

.line-wrap {
  position: relative;
}

.line {
  position: relative;
  height: 10px;
  border-radius: 999px;
  background: var(--border);
  overflow: hidden;
  margin: 0 0.35rem;
}

.fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--teal-deep), var(--teal-bright));
  transition: width 0.4s ease;
  min-width: 10px;
}

.stages {
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  display: flex;
  justify-content: space-between;
}

.stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
}

.tick {
  width: 10px;
  height: 10px;
  margin-top: -11px;
  border-radius: 50%;
  background: var(--cream);
  border: 2px solid var(--border);
  z-index: 1;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.stage.done .tick {
  background: var(--teal);
  border-color: var(--teal-deep);
}

.stage.current .tick {
  background: var(--teal-bright);
  border-color: var(--teal-deep);
  transform: scale(1.35);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.label {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted);
  text-align: center;
  white-space: nowrap;
}

.stage.done .label {
  color: var(--ink-soft);
}

.stage.current .label {
  color: var(--teal-deep);
  font-weight: 700;
}

@media (max-width: 700px) {
  .label {
    font-size: 0.58rem;
  }

  .phase-now {
    font-size: 0.7rem;
  }
}
</style>
